/* eslint-disable consistent-return */
import { Grid, Stack, Tooltip, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import { groupBy, isNil, isUndefined, keyBy, maxBy, orderBy, sum, sumBy, uniq, uniqBy, round, max } from 'lodash'
import moment from 'moment'
import 'moment/locale/fr'
import PropTypes from 'prop-types'
import DtoAnalysis from 'quality/dto/analyse/DtoAnalysis'
import DtoSearchIndices from 'quality/dto/DtoSearchIndices'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import RadioButtons from '../../../../components/forms/RadioButtons'
import Select from '../../../../components/forms/Select'
import CartographyPanel from '../../../../components/map/CartographyPanel'
import DtoQualityThreshold from '../../../../station/components/suivipc/qualitometer/dto/DtoQualityThreshold'
import { calculateAverage, calculateValue, filterQuantification, filterThresholdLevel, filterValid, getLocalizationPicto, getStatusAndResult, searchMaxAnalysis, searchMinAnalysis, searchP90Analysis } from '../../../../utils/AnalyseUtils'
import { getDate, getFullDate, getYear } from '../../../../utils/DateUtil'
import { hasValue } from '../../../../utils/NumberUtil'
import { CHIM_STATE, getStateLabels, GOOD, VERYGOOD } from '../../../../utils/QualityUtils'
import DtoQualitometerState from '../../../dto/states/DtoQualitometerState'
import DtoSearchHydrobio from 'quality/dto/taxons/DtoSearchHydrobio'
import { TAXON_MAP } from 'carto/constants/CartoConstants'
import { AbundanceLegend, OccurenceFrequencyLegend, PresenceMissingLegend } from 'components/map/utils/TaxonPredefinedMap'
import { calculateTaxonThresholdsResult, calculateThresholdsResult } from 'utils/QualityThresholdUtils'
import { RESEARCH_TYPE } from 'quality/constants/QualityConstants'
import ExportFileModal from 'components/modal/ExportFileModal'
import useBoolean from 'utils/customHook/useBoolean'
import useActions from 'utils/customHook/useActions'
import ExportAction from 'export/actions/ExportAction'
import { formatData } from 'utils/ExportDataUtil'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import useListIndexed from 'utils/customHook/useListIndexed'
import { secondaryBlue } from 'utils/constants/ColorTheme'

moment.locale('fr')

const MOST_DOWNGRADING = 'MOST_DOWNGRADING'
const YEAR = 'YEAR'

const getTooltip = (station, date) => {
    if (!station) {
        return ''
    }
    return (
        <Stack>
            <Typography>{`${i18n.code}: ${station.code || ''}`}</Typography>
            <Typography>{`${i18n.date}: ${getDate(date)}`}</Typography>
            <Typography>{`${i18n.name}: ${station.name || ''}`}</Typography>
        </Stack>
    )
}

const Label = styled('label')(({ sx }) => ({ fontSize: '1.5rem', color: 'black', ...sx }))

const Stats = ({
    analysis,
}) => {
    const {
        settings,
        qualitometers,
    } = useSelector(store => ({
        settings: store.AdministrationReducer.settings,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const validAnalysis = filterValid(analysis)

    const p90 = searchP90Analysis(validAnalysis)
    const minValue = searchMinAnalysis(validAnalysis)
    const maxValue = searchMaxAnalysis(validAnalysis)
    const average = calculateAverage(validAnalysis, settings)

    const minValueStation = qualitometers.find(q => q.id === minValue?.qualitometer)
    const minValueTooltip = getTooltip(minValueStation, minValue?.analysisDate)

    const maxValueStation = qualitometers.find(q => q.id === maxValue?.qualitometer)
    const maxValueTooltip = getTooltip(maxValueStation, maxValue?.analysisDate)

    const p90Station = qualitometers.find(q => q.id === p90?.qualitometer)
    const p90Tooltip = getTooltip(p90Station, p90?.analysisDate)

    return (
        <>
            <Grid container size={6} justifyContent='flex-end'>
                <Label>{i18n.min}:</Label>
            </Grid>
            <Grid size={6}>
                <Tooltip title={minValueTooltip}>
                    <Label sx={{ cursor: 'pointer', color: secondaryBlue }}>{calculateValue(minValue)}</Label>
                </Tooltip>
            </Grid>
            <Grid container size={6} justifyContent='flex-end'>
                <Label>{i18n.average}:</Label>
            </Grid>
            <Grid size={6}>
                <Label>{average ?? ''}</Label>
            </Grid>
            <Grid container size={6} justifyContent='flex-end'>
                <Label>{i18n.max}:</Label>
            </Grid>
            <Grid size={6}>
                <Tooltip title={maxValueTooltip}>
                    <Label sx={{ cursor: 'pointer', color: secondaryBlue }}>{calculateValue(maxValue)}</Label>
                </Tooltip>
            </Grid>
            <Grid container size={6} justifyContent='flex-end'>
                <Label>{i18n.percentile90}:</Label>
            </Grid>
            <Grid size={6}>
                <Tooltip title={p90Tooltip}>
                    <Label sx={{ cursor: 'pointer', color: secondaryBlue }}>{calculateValue(p90)}</Label>
                </Tooltip>
            </Grid>
        </>
    )
}

Stats.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.shape({
        // DtoAnalysis ou DtoAnalysisLight
        // calculateThresholdsResult
    })),
}

const ParameterSelectionTab = ({
    analysis = [],
    parameterAnalysis = [],
    listParameters,
    parameter,
    criteriaValue,
    hideQuantifications = false,

    onChangeParameter = () => { },
    onChangeCriteriaValue = () => { },
}) => {
    const {
        parameters,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const parametersIndex = useMemo(() => keyBy(parameters, 'code'), [parameters])

    const parameterOptions = useMemo(() => {
        const listParam = listParameters ?? uniqBy(analysis, 'parameter').map(a => a.parameter)
        const uniqParam = listParam.map(code => ({
            value: code,
            label: parametersIndex[code]?.name,
        })).filter(p => !isNil(p.value))
        return orderBy(uniqParam, ['label'])
    }, [analysis, listParameters, parametersIndex])

    const valueOptions = useMemo(() => [
        { value: '0', label: i18n.lastData },
        { value: '1', label: i18n.max },
        { value: '2', label: i18n.min },
        { value: '3', label: i18n.average },
        { value: '4', label: i18n.percentile90 },
    ], [])

    const nbQuantifications = filterValid(filterQuantification(analysis)).length

    const selectedParameterAnalysis = useMemo(() => parameterAnalysis.filter(a => a.parameter === parameter), [parameter, parameterAnalysis])

    return (
        <>
            <div className='row card'>
                <div className='card-content'>
                    <Grid container rowSpacing={0} columnSpacing={2}>
                        <Grid size={12}>
                            <SuperMultiAutocomplete
                                label={i18n.parameter}
                                values={parameter}
                                options={parameterOptions}
                                keyValue='value'
                                placeholder='Aucun paramètre'
                                onChange={onChangeParameter}
                            />
                        </Grid>
                        {!hideQuantifications && (
                            <>
                                <Grid container size={6} justifyContent='flex-end'>
                                    <Label>{i18n.nbQuantifications}:</Label>
                                </Grid>
                                <Grid size={6}>
                                    <Label>{nbQuantifications}</Label>
                                </Grid>
                            </>
                        )}
                        {
                            !isNil(parameter) && (
                                <Stats
                                    analysis={selectedParameterAnalysis}
                                />
                            )
                        }
                    </Grid>
                </div>
            </div>
            <div className='row card'>
                <div className='card-content'>
                    <Grid container>
                        <Grid size={12}>
                            <SuperMultiAutocomplete
                                label={i18n.criteriaValue}
                                values={criteriaValue}
                                options={valueOptions}
                                keyValue='value'
                                onChange={onChangeCriteriaValue}
                            />
                        </Grid>
                    </Grid>
                </div>
            </div>
        </>
    )
}

ParameterSelectionTab.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.shape({
        // DtoAnalysis ou DtoAnalysisLight
        // calculateThresholdsResult
    })),
    parameterAnalysis: PropTypes.arrayOf(PropTypes.shape({
        // DtoAnalysis ou DtoAnalysisLight
        // calculateThresholdsResult
    })),
    listParameters: PropTypes.arrayOf(PropTypes.string),
    parameter: PropTypes.string,
    criteriaValue: PropTypes.string,
    hideQuantifications: PropTypes.bool,

    onChangeParameter: PropTypes.func,
    onChangeCriteriaValue: PropTypes.func,
}

const CartoAnalysisPanel = ({
    analysis = [],
    listParameters,
    thresholds = [],
    researchType = RESEARCH_TYPE.WATER,
    filter = {},
    researchCriterias = {},
}) => {
    const [parameter, setParameter] = useState()
    const [criteriaValue, setCriteriaValue] = useState('0')

    const {
        selections,
        qualitometers,
        cities,
        units,
        contributors,
        parameters,
        qualifications,
        remarks,
        status,
        fractions,
        supports,
        typeEnvironmentModels,
        departments,
    } = useSelector(store => ({
        selections: store.ParameterReducer.selections,
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
        units: store.UnitReducer.units,
        contributors: store.ContributorReducer.contributors,
        parameters: store.ParameterReducer.parameters,
        qualifications: store.QualityReducer.qualifications,
        remarks: store.OperationReducer.remarks,
        status: store.QualityReducer.status,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
        departments: store.HomeReducer.departments,
    }), shallowEqual)

    const dispatch = useDispatch()

    const nbAllElement = analysis.length

    const {
        value: isExportModalOpen,
        setTrue: openExportModal,
        setFalse: closeExportModal,
    } = useBoolean(false)

    const qualitometersIndex = useMemo(() => keyBy(qualitometers, 'id'), [qualitometers])
    const contributorsIndex = useMemo(() => keyBy(contributors, 'id'), [contributors])
    const parametersIndex = useMemo(() => keyBy(parameters, 'code'), [parameters])
    const unitsIndex = useMemo(() => keyBy(units, 'id'), [units])
    const citiesIndex = useMemo(() => keyBy(cities, 'id'), [cities])
    const supportsIndex = useMemo(() => keyBy(supports, 'id'), [supports])
    const fractionsIndex = useMemo(() => keyBy(fractions, 'id'), [fractions])

    useActions(() => {
        return {
            exportList: [{
                onClick: openExportModal,
                label: i18n.excel,
            }],
        }
    }, [])

    const exportTypes = useMemo(() => {
        const models = typeEnvironmentModels.map(model => ({
            name: model,
            formats: [{
                type: i18n.excelFile,
                callback: () => {
                    if (nbAllElement > 20000) {
                        dispatch(ExportAction.exportResearchModelWithTmpExport(researchCriterias, model))
                    } else {
                        dispatch(ExportAction.exportResearchModel(researchCriterias, model))
                    }
                },
            }],
        }))

        return [
            {
                name: i18n.resultsTable,
                formats: [{
                    type: i18n.excelFile,
                    callback: () => dispatch(ExportAction.exportResearchFullCSV(researchCriterias, 'xlsx')),
                }, {
                    type: i18n.csvFile,
                    callback: () => dispatch(ExportAction.exportResearchFullCSV(researchCriterias, 'csv')),
                }],
            },
            ...models,
        ]
    }, [citiesIndex, contributorsIndex, departments, analysis, fractionsIndex, parametersIndex, qualifications, qualitometersIndex, remarks, researchCriterias, status, supportsIndex, typeEnvironmentModels, unitsIndex, nbAllElement])

    const {
        selection = '-1',
        // selectionResults = [],
        // parameters: selectedParameters = [],
    } = filter

    const filteredAnalysis = useMemo(() => {
        // const filtered = !isNil(parameter) ? analysis.filter(a => a.parameter === parameter) : analysis
        return orderBy(analysis, 'analysisDate', 'desc')
    }, [analysis])

    const nbParameters = uniqBy(analysis, 'parameter').length

    const stationsPanelTitle = useMemo(() => {
        const param = parametersIndex[parameter]
        if (selection !== '-1') {
            const labelSelection = selections.find(s => s.id === selection)
            return `${labelSelection?.name || labelSelection?.code || selection} ${param?.name || ''}`
        }
        return param?.name || i18n.noParameter
    }, [parameter, parametersIndex, selection, selections])

    return (
        <>
            <CartographyPanel
                layers={['STATIONS_POINTS']}
                analysis={filteredAnalysis}
                stationsPanelTitle={stationsPanelTitle}
                researchType={researchType}
                qualityThresholds={thresholds}
                componentType={'quality'}
                colorMode
                sortingType={criteriaValue}
                selectedParameter={parameter}
                panel={nbParameters > 0 ? (
                    <ParameterSelectionTab
                        analysis={analysis}
                        parameterAnalysis={filteredAnalysis}
                        listParameters={listParameters}
                        parameter={parameter}
                        onChangeParameter={setParameter}
                        criteriaValue={criteriaValue}
                        onChangeCriteriaValue={setCriteriaValue}
                        hideQuantifications={researchType === RESEARCH_TYPE.INDICES}
                    />
                ) : undefined}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={closeExportModal}
                data={exportTypes}
                closeOnExport
            />
        </>
    )
}

CartoAnalysisPanel.propTypes = {
    analysis: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysis)),
        PropTypes.shape({
            // like DtoAnalysis,
        }),
    ]),
    listParameters: PropTypes.arrayOf(PropTypes.string),
    thresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
    thresholdLevel: PropTypes.string,
    researchType: PropTypes.number,
    filter: PropTypes.shape({
        parameters: PropTypes.arrayOf(PropTypes.string),
        selection: PropTypes.string,
        selectionResults: PropTypes.arrayOf(PropTypes.string),
    }),
    researchCriterias: PropTypes.shape({}),
}

const StateTab = ({
    states = [],
    state,
    setState = () => { },
    displayMode,
    setDisplayMode = () => { },
    year,
    setYear = () => { },
}) => {
    const formattedStates = uniqBy(states, 'stateId').map(s => ({ code: s.stateId, name: s.stateName }))
    const options = [
        { code: MOST_DOWNGRADING, name: i18n.chooseMostDowngrading },
        { code: YEAR, name: i18n.chooseYear },
    ]

    const years = orderBy(uniq(states.filter(s => s.stateId === state).map(s => getYear(s.startDate))), x => x, 'desc')

    return (
        <Grid container rowSpacing={2} sx={{ paddingTop: '10px' }}>
            <Grid size={12}>
                <Select
                    options={formattedStates}
                    label={i18n.state}
                    value={state}
                    onChange={v => {
                        setState(v)

                        const newYearList = uniq(states.filter(s => s.stateId === v).map(s => getYear(s.startDate)))
                        if (!newYearList.includes(year)) {
                            setYear(max(newYearList))
                        }
                    }}
                />
            </Grid>
            <Grid size={12}>
                <RadioButtons
                    elements={options}
                    title={i18n.displayMode}
                    selected={displayMode}
                    onChange={setDisplayMode}
                />
            </Grid>
            <Grid size={4}>
                <Select
                    options={years.map(s => ({ code: s, name: s }))}
                    label={i18n.year}
                    disabled={displayMode !== YEAR}
                    integerValue
                    value={year}
                    onChange={setYear}
                    noSort
                />
            </Grid>
        </Grid>
    )
}

StateTab.propTypes = {
    states: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometerState)),
    state: PropTypes.number,
    setState: PropTypes.func,
    displayMode: PropTypes.string,
    setDisplayMode: PropTypes.func,
    year: PropTypes.number,
    setYear: PropTypes.func,
}

const CartoStatesPanel = ({
    states = [],
    researchCriterias = {},
}) => {
    const [state, setState] = useState(states[0]?.stateId)
    const [displayMode, setDisplayMode] = useState(MOST_DOWNGRADING)
    const [year, setYear] = useState(max(states.map(s => moment(s.startDate).year())))

    const {
        cities,
        qualitometers,
        typeEnvironmentModels,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
        qualitometers: store.QualityReducer.qualitometersLight,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
    }), shallowEqual)

    const dispatch = useDispatch()

    const {
        value: isExportModalOpen,
        setTrue: openExportModal,
        setFalse: closeExportModal,
    } = useBoolean(false)

    useActions(() => {
        return {
            exportList: [{
                onClick: openExportModal,
                label: i18n.excel,
            }],
        }
    }, [])

    const citiesIndex = useMemo(() => keyBy(cities, 'id'), [cities])
    const qualitometersIndex = useMemo(() => keyBy(qualitometers, 'id'), [qualitometers])

    const stateLabels = useMemo(getStateLabels, [])
    const allYears = orderBy(uniq(states.map(s => moment(s.startDate).year())), x => x)
    const headers = ['station', 'city', 'state', ...allYears.map(y => `${y}`)]

    const stateGroup = groupBy(states, s => `${s.stateId}:${s.qualitometer}`)
    const statesFormated = orderBy(Object.keys(stateGroup).map(key => {
        const [, qualitometerId] = key.split(':').map(id => parseInt(id))
        const group = stateGroup[key]
        const years = group.reduce((acc, s) => {
            acc[getYear(s.startDate)] = {
                ...(stateLabels[state.value] || { classNameColor: 'white' }),
                tooltip: state.comment,
            }
            return acc
        }, {})
        const station = qualitometersIndex[qualitometerId]
        return {
            station: { value: station ? `[${station.code || ''}]${station.name || ''}` : '' },
            city: { value: station ? citiesIndex[station.townCode]?.labelWithCode : '' },
            state: { value: group[0].stateName },
            order: group[0].order,
            ...years,
        }
    }), 'order')

    const exportStates = extension => {
        const dataWithHeaders = statesFormated.length ? [
            { ...statesFormated[0], headers },
            ...statesFormated.slice(1),
        ] : []
        dispatch(ExportAction.export(formatData(dataWithHeaders), extension, i18n.statesDetail))
    }

    const exportTypes = useMemo(() => {
        const models = typeEnvironmentModels.map(model => ({
            name: model,
            formats: [{
                type: i18n.excelFile,
                callback: () => dispatch(ExportAction.exportResearchModel(researchCriterias, model)),
            }],
        }))
        return [
            {
                name: i18n.resultsTable,
                formats: [{
                    type: i18n.excelFile,
                    callback: () => exportStates('xlsx'),
                }, {
                    type: i18n.csvFile,
                    callback: () => exportStates('csv'),
                }],
            },
            {
                name: `${i18n.exportFull} CSV`,
                formats: [{
                    type: i18n.csvFile,
                    callback: () => {
                        dispatch(ExportAction.exportResearchFullCSV(researchCriterias))
                        closeExportModal()
                    },
                }],
            },
            ...models,
        ]
    }, [researchCriterias, typeEnvironmentModels, statesFormated])

    const pointStates = useMemo(() => {
        const filteredStates = states
            .filter(s => s.stateId === state)
            .filter(s => displayMode === MOST_DOWNGRADING || getYear(s.startDate) === year)
        const group = groupBy(filteredStates, 'qualitometer')
        return Object.keys(group).map(key => {
            const qualito = qualitometersIndex[key]
            if (!qualito) {
                return
            }
            const worst = maxBy(group[key], 'value')
            const stateLabel = stateLabels[worst.value] || { classNameColor: 'white' }
            const isChimVeryGood = (worst.stateId === CHIM_STATE && worst.value === GOOD) // on chimical State there's only good and bad
            return {
                ...qualito,
                markerIcon: `pictures/markers/map_qualite_${isChimVeryGood ? 'blue' : stateLabel.classNameColor}.png`,
                state: isChimVeryGood ? VERYGOOD : stateLabel?.value,
                lastMeasure: { value: stateLabel?.value, date: getYear(worst.startDate) },
                keepDateUnchanged: true,
            }
        }).filter(s => !!s)
    }, [displayMode, qualitometersIndex, state, stateLabels, states, year])

    return (
        <>
            <CartographyPanel
                layers={['STATIONS_POINTS']}
                stationsPoints={pointStates}
                componentType={'quality'}
                panel={<StateTab
                    states={states}
                    state={state}
                    setState={setState}
                    displayMode={displayMode}
                    setDisplayMode={setDisplayMode}
                    year={year}
                    setYear={setYear}
                />}
                tableHeaders={['code', 'name', 'city', 'state']}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={closeExportModal}
                data={exportTypes}
            />
        </>
    )
}

CartoStatesPanel.propTypes = {
    states: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualitometerState)),
    researchCriterias: PropTypes.shape({}),
}

const CartoIndicesPanel = ({
    indices = [],
    thresholds,
    thresholdLevel,
    researchType,
    filter,
    researchCriterias = {},
}) => {
    const {
        units,
        parameters,
        typeEnvironmentModels,
        supports,
    } = useSelector(store => ({
        units: store.UnitReducer.units,
        parameters: store.ParameterReducer.parameters,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
        supports: store.SupportReducer.supports,
    }), shallowEqual)

    const dispatch = useDispatch()

    const indicesAnalysis = indices.map(i => ({
        accreditation: i.accreditation,
        operation: i.idOperation,
        parameter: i.parameter,
        qualification: i.qualification,
        remark: i.remarkCode,
        result: i.result,
        sampleDate: i.sampleDate,
        analysisDate: i.startDate || i.sampleDate,
        qualitometer: i.stationId,
        status: i.status,
        unit: i.unit,
    }))
    const {
        value: isExportModalOpen,
        setTrue: openExportModal,
        setFalse: closeExportModal,
    } = useBoolean(false)

    useActions(() => {
        return {
            exportList: [{
                onClick: openExportModal,
                label: i18n.excel,
            }],
        }
    }, [])

    const formattedAnalysis = useMemo(() => {
        const formatted = indicesAnalysis.map(a => ({ ...a, ...calculateThresholdsResult(a, thresholds) }))
        return hasValue(thresholdLevel) ? formatted.filter(a => filterThresholdLevel(a, thresholdLevel)) : formatted
    }, [indicesAnalysis, thresholdLevel, thresholds])

    const parametersIndex = useMemo(() => keyBy(parameters, 'code'), [parameters])
    const unitsIndex = useMemo(() => keyBy(units, 'id'), [units])
    const indexedSupports = useListIndexed(supports, 'code')

    const indicesFormated = useMemo(() => {
        return indices.map(indice => {
            const formattedIndice = { parameter: indice.parameter, remark: indice.remarkCode, result: indice.result, unit: indice.unit }
            const indiceResult = {
                ...formattedIndice,
                ...calculateThresholdsResult(formattedIndice, thresholds),
            }
            const parameter = parametersIndex[indice.parameter]
            return {
                stationCode: { value: indice.stationCode },
                stationName: { value: indice.stationName },
                sampleDate: { value: getFullDate(indice.startDate), cellType: 'right' },
                parameter: { value: parameter?.labelWithCode },
                statusAndResult: {
                    value: getStatusAndResult(indice, indiceResult.value, indiceResult.color, true),
                    tooltip: (
                        <TooltipAnalyse
                            title={parameter?.shortLabelWithCode || ''}
                            analyse={indiceResult}
                            threshold={indiceResult.threshold}
                        />
                    ),
                    sortValue: indice.result,
                    noPadding: true,
                },
                unit: { value: unitsIndex[indice.unit]?.symbol },
                support: { value: indexedSupports[indice.support]?.name },
                nullValue: { value: getLocalizationPicto(indice.localization) },
                result: { value: indiceResult.value, color: indiceResult.color },
            }
        })
    }, [indexedSupports, indices, parametersIndex, thresholds, unitsIndex])

    const exportIndices = extension => {
        const dataWithHeaders = indicesFormated.length ? [
            { ...indicesFormated[0], headers: ['stationCode', 'stationName', 'sampleDate', 'parameter', 'result', 'unit'] },
            ...indicesFormated.slice(1),
        ] : []
        dispatch(ExportAction.export(formatData(dataWithHeaders), extension, i18n.indicesDetail))
    }

    const exportTypes = useMemo(() => {
        const models = typeEnvironmentModels.map(model => ({
            name: model,
            formats: [{
                type: i18n.excelFile,
                callback: () => dispatch(ExportAction.exportResearchModel({ ...researchCriterias, stations: researchCriterias.stationsIds }, model)),
            }],
        }))
        return [
            {
                name: i18n.resultsTable,
                formats: [{
                    type: i18n.excelFile,
                    callback: () => exportIndices('xlsx'),
                }, {
                    type: i18n.csvFile,
                    callback: () => exportIndices('csv'),
                }],
            },
            {
                name: `${i18n.exportFull} CSV`,
                formats: [{
                    type: i18n.csvFile,
                    callback: () => {
                        dispatch(ExportAction.exportResearchFullCSV(researchCriterias))
                        closeExportModal()
                    },
                }],
            },
            ...models,
        ]
    }, [dispatch, researchCriterias, typeEnvironmentModels, indicesFormated])

    return (
        <>
            <CartoAnalysisPanel
                analysis={formattedAnalysis}
                thresholds={thresholds}
                thresholdLevel={thresholdLevel}
                researchType={researchType}
                filter={filter}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={closeExportModal}
                data={exportTypes}
            />
        </>
    )
}

CartoIndicesPanel.propTypes = {
    indices: PropTypes.arrayOf(PropTypes.instanceOf(DtoSearchIndices)),
    thresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
    thresholdLevel: PropTypes.string,
    researchType: PropTypes.number,
    filter: PropTypes.shape({
        parameters: PropTypes.arrayOf(PropTypes.string),
        selection: PropTypes.string,
        selectionResults: PropTypes.arrayOf(PropTypes.string),
    }),
    researchCriterias: PropTypes.shape({}),
}

const HydrobioSelectionTab = ({
    formattedHydrobios = [],
    filteredHydrobios = [],
    taxon,
    criteriaValue,
    map,

    onChangeTaxon = () => { },
    onChangeCriteriaValue = () => { },
    onChangeMap = () => { },
}) => {
    const {
        taxonsList,
    } = useSelector(store => ({
        taxonsList: store.TaxonReducer.taxons,
    }), shallowEqual)

    const taxonsIndex = useMemo(() => keyBy(taxonsList, 'code'), [taxonsList])

    const taxonOptions = useMemo(() => {
        const listTax = uniqBy(formattedHydrobios, 'taxon').map(t => t.taxon)
        const uniqTax = listTax.map(code => ({
            value: code,
            label: taxonsIndex[code]?.labelWithCode,
        })).filter(p => !isNil(p.value))
        return orderBy(uniqTax, ['label'])
    }, [formattedHydrobios, taxonsIndex])

    const valueOptions = useMemo(() => [
        { id: '0', label: i18n.lastData },
        { id: '1', label: i18n.max },
        { id: '2', label: i18n.min },
        { id: '3', label: i18n.average },
        { id: '4', label: i18n.percentile90 },
    ], [])

    const hydrobiosFiltered = !isUndefined(taxon) ? formattedHydrobios.filter(t => t.taxon === taxon) : formattedHydrobios
    const selectedTaxonHydrobios = useMemo(() => filteredHydrobios.filter(t => t.taxon === taxon), [filteredHydrobios, taxon])
    const nbQuantifications = filterValid(filterQuantification(hydrobiosFiltered)).length

    const getSumEnumerationsHydrobio = () => {
        const hydrobioListFiltered = formattedHydrobios.filter(t => t.taxon === taxon)
        return sumBy(hydrobioListFiltered, 'result')
    }
    const nbEnumerations = !isUndefined(taxon) ? getSumEnumerationsHydrobio() : sum(formattedHydrobios.flatMap(t => [ t.enumerationA, t.enumerationB, t.enumerationC, t.enumerationC2 ]).filter(e => !!e))

    const predifineMaps = useMemo(() => {
        return [
            { id: '0', label: i18n.any },
            { id: TAXON_MAP.PRESENCE_MISSING, label: i18n.presenceMissing },
            { id: TAXON_MAP.ABUNDANCE, label: i18n.abundance },
            { id: TAXON_MAP.OCCURENCE_FREQUENCY, label: i18n.occurrenceFrequency },
        ]
    }, [])

    return (
        <>
            <div className='row card'>
                <div className='card-content'>
                    <Grid container rowSpacing={0} columnSpacing={2}>
                        <Grid size={12}>
                            <SuperMultiAutocomplete
                                label={i18n.taxons}
                                values={taxon}
                                options={taxonOptions}
                                keyValue='value'
                                onChange={v => {
                                    onChangeTaxon(v)
                                    if (isUndefined(v)) {
                                        onChangeMap('0')
                                    }
                                }}
                                placeholder={i18n.noTaxon}
                            />
                        </Grid>
                        <Grid container size={6} justifyContent='flex-end'>
                            <Label>{i18n.nbQuantifications}:</Label>
                        </Grid>
                        <Grid size={6}>
                            <Label>{nbQuantifications}</Label>
                        </Grid>
                        <Grid container size={6} justifyContent='flex-end'>
                            <Label>{i18n.countsSum}:</Label>
                        </Grid>
                        <Grid size={6}>
                            <Label>{nbEnumerations}</Label>
                        </Grid>
                        {
                            !isNil(taxon) && (
                                <Stats
                                    analysis={selectedTaxonHydrobios}
                                />
                            )
                        }
                    </Grid>
                </div>
            </div>
            <div className='row card'>
                <div className='card-content'>
                    <Grid container>
                        <Grid size={12}>
                            <SuperMultiAutocomplete
                                label={i18n.criteriaValue}
                                values={criteriaValue}
                                options={valueOptions}
                                onChange={onChangeCriteriaValue}
                            />
                        </Grid>
                    </Grid>
                </div>
            </div>
            <div className='row card'>
                <div className='card-content'>
                    <Grid container>
                        <Grid size={12}>
                            <SuperMultiAutocomplete
                                label={i18n.predifineMaps}
                                values={map}
                                options={predifineMaps}
                                onChange={onChangeMap}
                                disabled={isUndefined(taxon)}
                            />
                        </Grid>
                        <Grid size={12}>
                            {map === TAXON_MAP.PRESENCE_MISSING && <PresenceMissingLegend />}
                            {map === TAXON_MAP.ABUNDANCE && <AbundanceLegend />}
                            {map === TAXON_MAP.OCCURENCE_FREQUENCY && <OccurenceFrequencyLegend />}
                        </Grid>
                    </Grid>
                </div>
            </div>
        </>
    )
}

HydrobioSelectionTab.propTypes = {
    formattedHydrobios: PropTypes.arrayOf(PropTypes.shape({
        // DtoSearchTaxon ou DtoSearchTaxonLight
        // calculateThresholdsResult
    })),
    filteredHydrobios: PropTypes.arrayOf(PropTypes.shape({
        // DtoSearchTaxon ou DtoSearchTaxonLight
        // calculateThresholdsResult
    })),
    taxon: PropTypes.string,
    criteriaValue: PropTypes.string,
    map: PropTypes.string,

    onChangeTaxon: PropTypes.func,
    onChangeCriteriaValue: PropTypes.func,
    onChangeMap: PropTypes.func,
}

const HYDROBIO_HEADER = ['stationCode', 'stationName', 'sampleDate', 'taxon', 'phaseA', 'phaseB', 'phaseC', 'phaseCbis', 'enumeration', 'abundance']

const CartoHydrobioPanel = ({
    hydrobioList = [],
    thresholds = [],
    thresholdLevel,
    researchType = 31,
    filter = {},
    researchCriterias = {},
}) => {
    const [taxon, setTaxon] = useState()
    const [criteriaValue, setCriteriaValue] = useState('0')
    const [map, setMap] = useState('0')

    const {
        taxonsList,
        qualitometers,
        selections,
        typeEnvironmentModels,
    } = useSelector(store => ({
        taxonsList: store.TaxonReducer.taxons,
        qualitometers: store.QualityReducer.qualitometersLight,
        selections: store.ParameterReducer.selections,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
    }), shallowEqual)

    const dispatch = useDispatch()

    const {
        selection = '-1',
    } = filter

    const {
        value: isExportModalOpen,
        setTrue: openExportModal,
        setFalse: closeExportModal,
    } = useBoolean(false)

    useActions(() => {
        return {
            exportList: [{
                onClick: openExportModal,
                label: i18n.excel,
            }],
        }
    }, [])

    const formattedHydrobios = useMemo(() => {
        const coloredHydrobios = hydrobioList.map(t => ({
            ...t,
            ...calculateTaxonThresholdsResult(t, thresholds),
        }))
        return hasValue(thresholdLevel) ? coloredHydrobios.filter(t => filterThresholdLevel(t, thresholdLevel)) : coloredHydrobios
    }, [hydrobioList, thresholdLevel, thresholds])

    const qualitometersIndex = useMemo(() => keyBy(qualitometers, 'id'), [qualitometers])
    const taxonsIndex = useMemo(() => keyBy(taxonsList, 'code'), [taxonsList])
    const formattedData = useMemo(() => {
        const groupedByOperation = groupBy(formattedHydrobios, l => `${l.qualitometer}#${l.operation}`)
        return formattedHydrobios.map(a => {
            const station = qualitometersIndex[a.qualitometer]
            const hydroTaxon = taxonsIndex[a.taxon]

            // const operationHydrobios = uniqBy(groupedByOperation[a.operation], 'taxon')
            const operationsPhases = sumBy(groupedByOperation[`${a.qualitometer}#${a.operation}`], 'result')

            const abundanceCalculated = operationsPhases && (a.result * 100) / operationsPhases
            const abundance = abundanceCalculated ? `${round(abundanceCalculated, 2)}%` : i18n.incalculable
            return {
                info: {
                    qualitometerId: a.qualitometer,
                    operationId: a.operation,
                    taxonId: a.taxon,
                },
                stationCode: { value: station?.code },
                stationName: { value: station?.name },
                sampleDate: { value: getFullDate(a.operationDate) },
                taxon: { value: hydroTaxon?.labelWithCode },
                phaseA: { value: a.enumerationA || 0 },
                phaseB: { value: a.enumerationB || 0 },
                phaseC: { value: a.enumerationC || 0 },
                phaseCbis: { value: a.enumerationC2 || 0 },
                enumeration: { value: a.result, color: a.color },
                abundance: { value: abundance },
            }
        })
    }, [qualitometersIndex, formattedHydrobios, taxonsIndex])

    const exportStates = extension => {
        const dataWithHeaders = formattedData.length ? [{ ...formattedData[0], headers: HYDROBIO_HEADER }, ...formattedData.slice(1)] : []
        dispatch(ExportAction.export(formatData(dataWithHeaders), extension, i18n.taxonsDetail))
    }

    const exportTypes = useMemo(() => {
        const models = typeEnvironmentModels.map(model => ({
            name: model,
            formats: [{
                type: i18n.excelFile,
                callback: () => dispatch(ExportAction.exportResearchModel(researchCriterias, model)),
            }],
        }))
        return [
            {
                name: i18n.resultsTable,
                formats: [{
                    type: i18n.excelFile,
                    callback: () => exportStates('xlsx'),
                }, {
                    type: i18n.csvFile,
                    callback: () => exportStates('csv'),
                }],
            },
            {
                name: `${i18n.exportFull} CSV`,
                formats: [{
                    type: i18n.csvFile,
                    callback: () => {
                        dispatch(ExportAction.exportResearchFullCSV(researchCriterias))
                        closeExportModal()
                    },
                }],
            },
            ...models,
        ]
    }, [dispatch, researchCriterias, typeEnvironmentModels, formattedData])

    const filteredHydrobios = useMemo(() => {
        // const filtered = !isNil(taxon) && map === '0' ? formattedHydrobios.filter(t => t.taxon === taxon) : formattedHydrobios
        return orderBy(formattedHydrobios, 'operationDate', 'desc')
    }, [formattedHydrobios])

    const nbHydrobios = uniqBy(hydrobioList, 'taxon').length

    const stationsPanelTitle = useMemo(() => {
        const tax = taxonsIndex[taxon]
        if (selection !== '-1') {
            const labelSelection = selections.find(s => s.id === selection)
            return `${labelSelection?.name || labelSelection?.code || selection} ${tax?.latinName || ''}`
        }
        return tax?.latinName || i18n.noTaxon
    }, [selection, selections, taxon, taxonsIndex])

    return (
        <>
            <CartographyPanel
                layers={['STATIONS_POINTS']}
                hydrobioList={filteredHydrobios}
                stationsPanelTitle={stationsPanelTitle}
                researchType={researchType}
                qualityThresholds={thresholds}
                componentType={'quality'}
                colorMode
                sortingType={criteriaValue}
                taxonMap={map}
                selectedTaxon={taxon}
                panel={nbHydrobios > 0 ? (
                    <HydrobioSelectionTab
                        formattedHydrobios={hydrobioList}
                        filteredHydrobios={filteredHydrobios}
                        taxon={taxon}
                        onChangeTaxon={setTaxon}
                        criteriaValue={criteriaValue}
                        onChangeCriteriaValue={setCriteriaValue}
                        map={map}
                        onChangeMap={setMap}
                    />
                ) : undefined}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={closeExportModal}
                data={exportTypes}
            />
        </>
    )
}

CartoHydrobioPanel.propTypes = {
    hydrobioList: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(DtoSearchHydrobio)),
        PropTypes.shape({
            // like DtoSearchHydrobio,
        }),
    ]),
    thresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),
    thresholdLevel: PropTypes.string,
    researchType: PropTypes.number,
    filter: PropTypes.shape({
        taxons: PropTypes.arrayOf(PropTypes.string),
        selection: PropTypes.string,
        selectionResults: PropTypes.arrayOf(PropTypes.string),
    }),
    researchCriterias: PropTypes.shape({}),
}

export {
    CartoAnalysisPanel,
    CartoStatesPanel,
    CartoIndicesPanel,
    CartoHydrobioPanel,
    ParameterSelectionTab,
}